import {useEffect, useMemo} from 'react';
import type {ComponentPropsWithoutRef, KeyboardEvent as ReactKeyboardEvent} from 'react';
import {useAuth} from '@/auth/useAuth';
import {withVisibleDesktop1100, withHiddenDesktop1100} from '@/hocs/withResponsiveUtils';
import UserDropDownMenu from 'components/user-dropdown-menu';
import {getUserFromWindow} from '@/auth/auth.store';
import {LogInCTA, RequestDemoCTA, StartTrialCTA, GoToAccountCTA} from './header-ctas';
import Avatar, {AVATAR_VARIANTS} from '../ui/avatar/index';
import type {HeaderUser, UserHeaderProps} from '../../types/components/header';
import styles from './header.module.scss';

type ResponsiveDivProps = ComponentPropsWithoutRef<'div'>;
const ResponsiveDiv = (props: ResponsiveDivProps) => <div {...props} />;
const VisibleOnMobile = withHiddenDesktop1100(ResponsiveDiv);
const VisibleOnDesktop = withVisibleDesktop1100(ResponsiveDiv);

const UserHeader = ({
  closeMenus,
  isNeutral50Header,
  userDropdownOpen,
  toggleUserDropdown,
  closeUserDropdown
}: UserHeaderProps) => {
  const {user: fetchedUser, logout} = useAuth();
  const windowAttachedUser = getUserFromWindow();

  const user = useMemo<HeaderUser | null>(
    () => ((windowAttachedUser || fetchedUser) as HeaderUser | null) || null,
    [fetchedUser, windowAttachedUser]
  );

  const variant = useMemo(
    () => (isNeutral50Header ? AVATAR_VARIANTS.SECONDARY : AVATAR_VARIANTS.PRIMARY),
    [isNeutral50Header]
  );

  const handleESC = (event: KeyboardEvent) => {
    if (event.key === 'Escape') closeUserDropdown(false);
  };

  const handleDropdownKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') closeUserDropdown(false);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleESC, false);

    return () => {
      document.removeEventListener('keydown', handleESC, false);
    };
  }, []);

  if (user) {
    return (
      <ul className={styles['nav-list']}>
        <li>
          <GoToAccountCTA
            accountName={user.account.name}
            onClickCb={closeMenus}
            href={user.account.dashboardUrl}
          />
        </li>
        <li className={styles['nav-user-wrapper']}>
          <VisibleOnMobile>
            <UserDropDownMenu
              user={user}
              isOpen={userDropdownOpen}
              variant={variant}
              onClickCb={toggleUserDropdown}
              onLogoutCb={logout}
            />
          </VisibleOnMobile>

          <VisibleOnDesktop data-testid="desktop-user-avatar">
            <Avatar
              hasPointerOnHover
              isActive={userDropdownOpen}
              user={user}
              variant={variant}
              isDropdownOpen={userDropdownOpen}
              onClickCb={toggleUserDropdown}
            />

            {userDropdownOpen && (
              <div
                role="menu"
                tabIndex={0}
                className={styles['user-dropdown-menu']}
                onClick={toggleUserDropdown}
                onKeyDown={handleDropdownKeyDown}>
                <div className={styles['user-dropdown-menu-inside-wrapper']}>
                  <UserDropDownMenu
                    user={user}
                    isDropdown
                    isOpen={userDropdownOpen}
                    variant={variant}
                    onLogoutCb={logout}
                  />
                </div>
              </div>
            )}
          </VisibleOnDesktop>
        </li>
      </ul>
    );
  }

  return (
    <ul className={styles['nav-list']}>
      <li>
        <LogInCTA onClickCb={closeMenus} />
      </li>
      <li>
        <StartTrialCTA onClickCb={closeMenus} />
      </li>
      <li>
        <RequestDemoCTA onClickCb={closeMenus} />
      </li>
    </ul>
  );
};

export default UserHeader;
